/**
 * Maps V2 customize wizard state → BookingService CreateBookingDto.
 * Persists flat columns + CustomizedDataJson / PriceBreakdownJson so services are not lost.
 */

import { resolveProductLine as resolveLine } from './productLineConfig';

const toYmd = (date) => {
  if (!date) return null;
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    return date.slice(0, 10);
  }
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDaysYmd = (ymd, days) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return toYmd(dt);
};

const resolveProductLine = (data) => resolveLine(data);

const hotelNightsTotal = (data) => {
  if (data.includeHotel === false) return 0;
  return (
    (data.includeFirstEntry ? Number(data.firstEntryNights) || 0 : 0) +
    (data.includeSecondEntry ? Number(data.secondEntryNights) || 0 : 0) +
    (data.includeThirdEntry ? Number(data.thirdEntryNights) || 0 : 0)
  );
};

const tripDurationDays = (data) => {
  const nights = hotelNightsTotal(data);
  if (nights > 0) return nights;
  if (data.includeCarRental && data.carRentalDays) return Math.max(1, Number(data.carRentalDays) || 1);
  return 1;
};

/**
 * Build the JSON bag stored in CustomizedDataJson (full fidelity for admin/ops).
 */
export const buildCustomizedDataObject = (data, priceCalculation) => {
  const productLine = resolveProductLine(data);
  const includeHotel = data.includeHotel !== false;

  return {
    uiVersion: 'v2-customize-2026-08',
    productLine,
    journeyType: data.journeyType || (productLine === 'Umrah' ? 'umrah' : 'travel'),
    journeyMode: 'Customize',
    tripScope:
      productLine === 'Local' ? 'Local' : productLine === 'Domestic' ? 'Domestic' : 'International',
    services: {
      includeFlight: !!data.includeFlight,
      includeHotel,
      includeTransport: !!data.includeTransport,
      includeHomePickup: !!data.includeHomePickup,
      includeVisa: !!data.includeVisa,
      includeCarRental: !!data.includeCarRental,
      includeZiaraat: !!data.includeZiaraat,
    },
    flight: data.includeFlight
      ? {
          origin: data.flightOrigin || null,
          destination: data.flightDestination || null,
          offerId: data.selectedFlightOfferId || null,
          amount: data.selectedFlightAmount != null ? Number(data.selectedFlightAmount) : null,
          currency: data.selectedFlightCurrency || null,
          label: data.selectedFlightLabel || null,
        }
      : null,
    visa: data.includeVisa ? { type: data.visaType || 'Tourist' } : null,
    carRental: data.includeCarRental
      ? {
          city: data.carRentalCity || null,
          days: Number(data.carRentalDays) || 1,
          carClass: data.carRentalClass || 'Sedan',
        }
      : null,
    homePickup: {
      enabled: !!data.includeHomePickup && !!data.includeTransport,
      notes: null,
    },
    ziaraat: data.includeZiaraat
      ? {
          enabled: true,
          makkahTier: data.makkahZiaraat || 'Standard',
          madinaTier: data.madinaZiaraat || 'Standard',
        }
      : { enabled: false },
    stays: includeHotel
      ? {
          packageType: data.packageType,
          firstEntry: data.includeFirstEntry
            ? { location: data.firstEntryLocation, nights: data.firstEntryNights }
            : null,
          secondEntry: data.includeSecondEntry
            ? { location: data.secondEntryLocation, nights: data.secondEntryNights }
            : null,
          thirdEntry: data.includeThirdEntry
            ? { location: data.thirdEntryLocation, nights: data.thirdEntryNights }
            : null,
        }
      : null,
    transport: data.includeTransport
      ? { mode: data.transportationType || data.transportChoice || null }
      : null,
    onlineSupport: !!data.onlineSupport,
    priceSnapshot: priceCalculation || null,
  };
};

/**
 * Price breakdown must include `totalPrice` — BookingService reads that key.
 */
export const buildPriceBreakdownObject = (data, priceCalculation) => {
  const productLine = resolveProductLine(data);
  const totalPrice =
    (priceCalculation && typeof priceCalculation.totalPrice === 'number'
      ? priceCalculation.totalPrice
      : null) ??
    (priceCalculation?.breakdown?.totalAmount != null
      ? Number(priceCalculation.breakdown.totalAmount)
      : 0);

  return {
    totalPrice: Number(totalPrice) || 0,
    currency:
      productLine === 'Domestic' || productLine === 'Local'
        ? 'PKR'
        : priceCalculation?.currency || 'USD',
    baseStay: priceCalculation?.baseStay ?? null,
    transport: priceCalculation?.transport ?? null,
    flight: priceCalculation?.flight ?? null,
    visa: priceCalculation?.visa ?? null,
    carRental: priceCalculation?.carRental ?? null,
    ziaraat: priceCalculation?.ziaraat ?? null,
    support: priceCalculation?.support ?? null,
    taxes: priceCalculation?.taxes ?? null,
    breakdown: priceCalculation?.breakdown || null,
    calculatedAt: new Date().toISOString(),
  };
};

/**
 * @param {object} data - customize wizard bookingData
 * @param {object|null} priceCalculation - from calculateCustomizedPrice / summary
 * @param {{ isGuestBooking?: boolean }} [options]
 * @returns {object} CreateBookingDto-compatible payload (camelCase JSON)
 */
export const buildCustomizedBookingPayload = (data, priceCalculation, options = {}) => {
  const includeHotel = data.includeHotel !== false;
  const start = toYmd(data.departureDate);
  if (!start) {
    throw new Error('Departure date is required');
  }

  const duration = tripDurationDays(data);
  const end = addDaysYmd(start, duration);
  const adults = Number(data.adults) || 1;
  const children = Number(data.children) || 0;
  const infants = Number(data.infants) || 0;
  const productLine = resolveProductLine(data);

  const customizedData = buildCustomizedDataObject(data, priceCalculation);
  const priceBreakdown = buildPriceBreakdownObject(data, priceCalculation);

  return {
    // Regular package path unused for customize
    packageId: '00000000-0000-0000-0000-000000000000',
    travelStartDate: start,
    travelEndDate: end,
    numberOfTravelers: adults + children + infants,
    couponCode: null,
    specialRequests: data.comments || '',
    travelerDetails: [],
    bookingRooms: [],
    isGuestBooking: options.isGuestBooking === true,

    isCustomizedPackage: true,
    departureCity: data.flyFrom || null,
    packageType: includeHotel ? data.packageType || null : null,
    adults,
    children,
    infants,

    firstEntryLocation: includeHotel && data.includeFirstEntry ? data.firstEntryLocation : null,
    firstEntryNights: includeHotel && data.includeFirstEntry ? Number(data.firstEntryNights) || 0 : null,
    includeFirstEntry: includeHotel && !!data.includeFirstEntry,
    secondEntryLocation: includeHotel && data.includeSecondEntry ? data.secondEntryLocation : null,
    secondEntryNights: includeHotel && data.includeSecondEntry ? Number(data.secondEntryNights) || 0 : null,
    includeSecondEntry: includeHotel && !!data.includeSecondEntry,
    thirdEntryLocation: includeHotel && data.includeThirdEntry ? data.thirdEntryLocation : null,
    thirdEntryNights: includeHotel && data.includeThirdEntry ? Number(data.thirdEntryNights) || 0 : null,
    includeThirdEntry: includeHotel && !!data.includeThirdEntry,

    transportChoice: data.includeTransport
      ? data.transportationType || data.transportChoice || null
      : null,
    ziaraatMakkah: data.includeZiaraat ? data.makkahZiaraat || null : null,
    ziaraatMadina: data.includeZiaraat ? data.madinaZiaraat || null : null,
    onlineSupport: !!data.onlineSupport,

    customerName: data.fullName || data.customerName || null,
    phoneNumber: data.phoneNumber || null,
    city: data.cityName || data.city || null,
    email: data.emailAddress || data.email || null,
    comments: data.comments || null,

    priceBreakdownJson: JSON.stringify(priceBreakdown),
    customizedDataJson: JSON.stringify({
      ...customizedData,
      // Keep productLine discoverable at top level inside JSON
      productLine,
    }),
  };
};

export default buildCustomizedBookingPayload;
